import "./global.css";

import { Toaster } from "@/components/ui/toaster";
import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";

const NotFound = lazy(() => import("./pages/NotFound"));
const Placeholder = lazy(() => import("./pages/Placeholder"));
const HardwoodFloor = lazy(() => import("./pages/HardwoodFloor"));
const Flagstone = lazy(() => import("./pages/Flagstone"));
const Pavers = lazy(() => import("./pages/Pavers"));
const Concrete = lazy(() => import("./pages/Concrete"));
const MarbleNaturalStone = lazy(() => import("./pages/MarbleNaturalStone"));
const TileGrout = lazy(() => import("./pages/TileGrout"));
const KitchenRemodeling = lazy(() => import("./pages/KitchenRemodeling"));
const BathroomRemodeling = lazy(() => import("./pages/BathroomRemodeling"));
const HardSurfaces = lazy(() => import("./pages/HardSurfaces"));
const FreeEstimate = lazy(() => import("./pages/FreeEstimate"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Projects = lazy(() => import("./pages/Projects"));
const Services = lazy(() => import("./pages/Services"));
const ClientList = lazy(() => import("./pages/ClientList"));
const MarbleRefinishing = lazy(() => import("./pages/MarbleRefinishing"));
const MarblePolishing = lazy(() => import("./pages/MarblePolishing"));
const MarbleCleaningSealing = lazy(
  () => import("./pages/MarbleCleaningSealing"),
);
const MarbleRepair = lazy(() => import("./pages/MarbleRepair"));
const MarbleStainRemoval = lazy(() => import("./pages/MarbleStainRemoval"));
const MarbleEtchRemoval = lazy(() => import("./pages/MarbleEtchRemoval"));
const ConcreteCleaningSealing = lazy(
  () => import("./pages/ConcreteCleaningSealing"),
);
const ConcreteGrindingPolishing = lazy(
  () => import("./pages/ConcreteGrindingPolishing"),
);
const ConcreteStaining = lazy(() => import("./pages/ConcreteStaining"));
const NaturalStoneRestoration = lazy(
  () => import("./pages/NaturalStoneRestoration"),
);
const NaturalStoneDeepCleaning = lazy(
  () => import("./pages/NaturalStoneDeepCleaning"),
);
const NaturalStoneRepair = lazy(() => import("./pages/NaturalStoneRepair"));
const NaturalStoneSealing = lazy(() => import("./pages/NaturalStoneSealing"));
const HardwoodRefinishing = lazy(() => import("./pages/HardwoodRefinishing"));
const HardwoodBuffRecoat = lazy(() => import("./pages/HardwoodBuffRecoat"));
const HardwoodDeepCleaning = lazy(() => import("./pages/HardwoodDeepCleaning"));
const HardwoodRepair = lazy(() => import("./pages/HardwoodRepair"));
const InstallationStoneTile = lazy(
  () => import("./pages/InstallationStoneTile"),
);
const InstallationHardwood = lazy(() => import("./pages/InstallationHardwood"));
const InstallationLaminateVinyl = lazy(
  () => import("./pages/InstallationLaminateVinyl"),
);
const InstallationStoneQuartz = lazy(
  () => import("./pages/InstallationStoneQuartz"),
);
const Warranty = lazy(() => import("./pages/Warranty"));
const HomeRemodeling = lazy(() => import("./pages/HomeRemodeling"));
const HardwoodFlooring = lazy(() => import("./pages/HardwoodFlooring"));
const Installation = lazy(() => import("./pages/Installation"));
const NaturalStone = lazy(() => import("./pages/NaturalStone"));
const MarbleRestoration = lazy(() => import("./pages/MarbleRestoration"));
const ConcreteRestoration = lazy(
  () => import("./pages/ConcreteRestoration"),
);
const CityService = lazy(() => import("./pages/CityService"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/services" element={<Services />} />
            <Route path="/kitchen-remodeling" element={<KitchenRemodeling />} />
            <Route
              path="/bathroom-remodeling"
              element={<BathroomRemodeling />}
            />
            <Route path="/hard-surfaces" element={<HardSurfaces />} />
            <Route path="/hardwood-floor" element={<HardwoodFloor />} />
            <Route path="/flagstone" element={<Flagstone />} />
            <Route path="/pavers" element={<Pavers />} />
            <Route path="/concrete" element={<Concrete />} />
            <Route
              path="/marble-natural-stone"
              element={<MarbleNaturalStone />}
            />
            <Route path="/tile-grout" element={<TileGrout />} />
            <Route path="/marble-refinishing" element={<MarbleRefinishing />} />
            <Route path="/marble-polishing" element={<MarblePolishing />} />
            <Route
              path="/marble-cleaning-sealing"
              element={<MarbleCleaningSealing />}
            />
            <Route path="/marble-repair" element={<MarbleRepair />} />
            <Route
              path="/marble-stain-removal"
              element={<MarbleStainRemoval />}
            />
            <Route
              path="/marble-etch-removal"
              element={<MarbleEtchRemoval />}
            />
            <Route
              path="/concrete-cleaning-sealing"
              element={<ConcreteCleaningSealing />}
            />
            <Route
              path="/concrete-grinding-polishing"
              element={<ConcreteGrindingPolishing />}
            />
            <Route path="/concrete-staining" element={<ConcreteStaining />} />
            <Route
              path="/natural-stone-restoration"
              element={<NaturalStoneRestoration />}
            />
            <Route
              path="/natural-stone-deep-cleaning"
              element={<NaturalStoneDeepCleaning />}
            />
            <Route
              path="/natural-stone-repair"
              element={<NaturalStoneRepair />}
            />
            <Route
              path="/natural-stone-sealing"
              element={<NaturalStoneSealing />}
            />
            <Route
              path="/hardwood-refinishing"
              element={<HardwoodRefinishing />}
            />
            <Route
              path="/hardwood-buff-recoat"
              element={<HardwoodBuffRecoat />}
            />
            <Route
              path="/hardwood-deep-cleaning"
              element={<HardwoodDeepCleaning />}
            />
            <Route path="/hardwood-repair" element={<HardwoodRepair />} />
            <Route
              path="/installation-stone-tile"
              element={<InstallationStoneTile />}
            />
            <Route
              path="/installation-hardwood"
              element={<InstallationHardwood />}
            />
            <Route
              path="/installation-laminate-vinyl"
              element={<InstallationLaminateVinyl />}
            />
            <Route
              path="/installation-stone-quartz"
              element={<InstallationStoneQuartz />}
            />
            <Route path="/home-remodeling" element={<HomeRemodeling />} />
            <Route
              path="/hardwood-flooring"
              element={<HardwoodFlooring />}
            />
            <Route path="/installation" element={<Installation />} />
            <Route path="/natural-stone" element={<NaturalStone />} />
            <Route
              path="/marble-restoration"
              element={<MarbleRestoration />}
            />
            <Route
              path="/concrete-restoration"
              element={<ConcreteRestoration />}
            />
            <Route path="/warranty" element={<Warranty />} />
            <Route path="/free-estimate" element={<FreeEstimate />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/client-list" element={<ClientList />} />
            <Route path="/contact" element={<ContactUs />} />
            {/* Dynamic city/service landing pages — must come AFTER all explicit routes */}
            <Route
              path="/:serviceCitySlug"
              element={<CityService />}
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

const rootElement = document.getElementById("root")!;

// Store root instance on a global object that persists across HMR updates
if (!(window as any).__roots) {
  (window as any).__roots = new Map();
}

const roots = (window as any).__roots as Map<
  Element,
  ReturnType<typeof createRoot>
>;

if (!roots.has(rootElement)) {
  const root = createRoot(rootElement);
  roots.set(rootElement, root);
}

roots.get(rootElement)!.render(<App />);
